/**
 * PRV-4 — `getVariableSourceLabel` — one-line source label per SED row.
 *
 * Pre-PRV the SEDVariableBrowser carried a `domain` label and a
 * status chip but no "who said this?" affordance on the row itself.
 * PRV-1/2/3 surfaced rich per-row provenance strips for the verified
 * tiers; PRV-4 closes the loop by producing a single human-readable
 * label that callers (Variable Browser row strip, Coverage Matrix
 * tool-tip, future audit CSV export) can render or query without
 * re-deriving the discriminator each time.
 *
 * The label is a pure projection from {`status`, `source`, primary
 * `linkedDocumentRefs[0]`, primary `authoredClaims[0]`,
 * `derivedFromReport`, `researchSource`} and follows this rank
 * (matches the chip-rank in `SEDVariableBrowser.tsx:STATUS_RANK`):
 *
 *   1. `verified-from-doc`     → `<docName>` (PRV-1 strip subject)
 *   2. `verified-from-context` → `Authored by <actor>` (PRV-2 strip)
 *   3. `verified-from-report`  → `<ReportType> v<version>`
 *   4. `verified-from-dam`     → `DAM v<version>`
 *   5. `verified-from-research`→ `<sourceTitle>` (DCO-4 chip)
 *   6. `verified`              → humanised `source` (provider name)
 *   7. `anomalous` / `unavailable` → `'—'` (no provenance to surface)
 *
 * Provider humanisation mirrors the table in
 * `evidenceLineage/requirementLineage.ts:humanizeProviderKey` so the
 * Coverage Matrix's chip and the Variable Browser's row agree on
 * "WRI Aqueduct" vs "wri-aqueduct" raw keys.
 *
 * Pure function — no I/O, no React. Never throws on malformed input;
 * returns `'—'` when no projection is available so the caller can
 * render a stable placeholder.
 *
 * Composability:
 *   - PRV-1/2/3 (shipped) — the per-row inline strips show the FULL
 *     provenance chunk (page + section + excerpt + Open-at-page).
 *     `getVariableSourceLabel` is the compact one-line version for
 *     surfaces that can't fit a strip (chip overlays, tooltips,
 *     CSV cells).
 *   - DCO Wave-A (shipped) — `verified-from-research` rows surface
 *     the AI-Research source title via `researchSource.sourceTitle`.
 *   - RDX (shipped) — `verified-from-report` rows surface the
 *     originating report's type + version through `derivedFromReport`.
 *   - EVL-6 (sibling) — `REQUIREMENT_TO_FIELDS` consumers can pivot
 *     from "which field?" to "what label?" via this helper.
 */
import type { SEDVariable } from '../types/sedAmendments';

/**
 * Humanise a provider key into a stable display name.
 *
 * Mirrors `humanizeProviderKey` in
 * `evidenceLineage/requirementLineage.ts` — kept duplicated rather
 * than imported to avoid a circular dep between sourceLabel (used by
 * the Browser) and requirementLineage (used by the Coverage chip).
 * Both will collapse onto a shared `providerLabels.ts` registry in a
 * future cleanup.
 */
function humanizeProviderKey(key: string | undefined | null): string {
  if (!key || typeof key !== 'string') return 'Provider';
  const k = key.toLowerCase().replace(/\s+/g, '-');
  if (k.includes('wri') || k.includes('aqueduct')) return 'WRI Aqueduct';
  if (k.includes('fema')) return 'FEMA NFHL';
  if (k.includes('nrel')) return 'NREL';
  if (k.includes('usgs')) return 'USGS';
  if (k.includes('eia')) return 'EIA';
  if (k.includes('hifld')) return 'HIFLD';
  if (k.includes('regrid')) return 'Regrid';
  if (k.includes('nws') || k.includes('noaa') || k.includes('open-meteo')) return 'NOAA / Open-Meteo';
  if (k.includes('peeringdb')) return 'PeeringDB';
  if (k.includes('airnow') || k.includes('aqi')) return 'AirNow';
  if (k.includes('usfws')) return 'USFWS';
  if (k.includes('nrc') || k.includes('adams')) return 'NRC ADAMS';
  // Fallback: hyphen → space, capitalise words. Keeps a sensible
  // display even for providers we haven't curated yet.
  return key
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map(w => (w.length === 0 ? w : w[0].toUpperCase() + w.slice(1)))
    .join(' ');
}

/** Format the human-readable actor for an authored claim row.
 *  Mirrors `formatActorLabel` inline in SEDVariableBrowser. */
function formatActorLabel(actor: { uid: string; email?: string } | string | undefined): string {
  if (!actor) return 'Site Context';
  if (typeof actor === 'string') return actor;
  return actor.email ?? actor.uid;
}

/** Format the report-type for `verified-from-report` labels. */
function humanizeReportType(reportType: string): string {
  switch (reportType) {
    case 'feasibility': return 'Screening Assessment';
    case 'environmental': return 'Environmental';
    case 'power-availability': return 'Power Availability';
    case 'water-resource': return 'Water Resource';
    case 'test-fit': return 'Test Fit';
    case 'interconnection': return 'Interconnection';
    default:
      return reportType
        .replace(/[_-]+/g, ' ')
        .split(' ')
        .map(w => (w.length === 0 ? w : w[0].toUpperCase() + w.slice(1)))
        .join(' ');
  }
}

/** Narrow shape so `getVariableSourceLabel` can be tested without
 *  building a full `SEDVariable`. */
export interface SourceLabelInput {
  status: SEDVariable['status'];
  source?: string;
  documentFieldSource?: SEDVariable['documentFieldSource'];
  linkedDocumentRefs?: SEDVariable['linkedDocumentRefs'];
  authoredClaims?: SEDVariable['authoredClaims'];
  derivedFromReport?: SEDVariable['derivedFromReport'];
  researchSource?: SEDVariable['researchSource'];
  /** Optional DAM provenance for `verified-from-dam` rows. */
  damSource?: SEDVariable['damSource'];
  /** Optional doc-id → filename resolver. When omitted, doc rows
   *  fall back to the raw docId — matches the SED browser's
   *  `resolveDocName` contract for legacy SEDs whose linked doc was
   *  evicted from the site. */
  resolveDocName?: (docId: string) => string | undefined;
}

/**
 * Return a single one-line source label for a SED row. Empty / no-
 * provenance rows resolve to `'—'` (stable placeholder).
 *
 * Examples (per status):
 *   - verified-from-doc       → 'Phase I ESA.pdf'
 *   - verified-from-context   → 'Authored by william@datacrate.io'
 *   - verified-from-report    → 'Feasibility v3'
 *   - verified-from-dam       → 'DAM v0001'   (* — Wave B placeholder
 *                                                 until damSource is
 *                                                 projected onto the
 *                                                 SEDVariable shape;
 *                                                 falls back to provider
 *                                                 label today)
 *   - verified-from-research  → 'AI Research — <sourceTitle>'
 *   - verified                → 'WRI Aqueduct'
 *   - anomalous / unavailable → '—'
 */
export function getVariableSourceLabel(input: SourceLabelInput): string {
  if (input.documentFieldSource) return input.documentFieldSource.docName;
  switch (input.status) {
    case 'verified-from-doc': {
      const primary = input.linkedDocumentRefs?.[0];
      if (!primary) return '—';
      const resolved = input.resolveDocName ? input.resolveDocName(primary.docId) : undefined;
      return resolved && resolved.length > 0 ? resolved : primary.docId;
    }
    case 'verified-from-context': {
      const claim = input.authoredClaims?.[0];
      if (!claim) return '—';
      return `Authored by ${formatActorLabel(claim.writtenBy)}`;
    }
    case 'verified-from-report': {
      const report = input.derivedFromReport;
      if (!report) return '—';
      const rt = humanizeReportType(report.reportType);
      return `${rt} v${report.reportVersion}`;
    }
    case 'verified-from-dam': {
      if (input.damSource?.damVersion) {
        return `DAM v${input.damSource.damVersion}`;
      }
      return 'DAM v0001';
    }
    case 'verified-from-research': {
      const research = input.researchSource;
      if (!research) return '—';
      const title = research.sourceTitle && research.sourceTitle.length > 0
        ? research.sourceTitle
        : research.sourceUrl;
      return `AI Research — ${title}`;
    }
    case 'verified': {
      return humanizeProviderKey(input.source);
    }
    case 'anomalous':
    case 'unavailable':
    default:
      return '—';
  }
}
/**
 * PRV-4a — `sourceLabel` pure tone resolver (row 0.99228 PRV).
 *
 * Single owner of the per-variable source-chip label + tone. PRV-1..3
 * shipped the underlying provenance projections (`linkedDocumentRefs`,
 * `authoredClaims`, `derivedFromReport`, `researchSource`) onto every
 * `SEDVariable` row; PRV-4a collapses that union into ONE chip API the
 * UI can render without re-walking the provenance shapes.
 *
 * Contract — pure, no React imports, no I/O:
 *   sourceLabel(variable) → { label, tone }
 *
 *   tone ∈ 'doc' | 'provider' | 'authored' | 'research' | 'dam' | 'missing'
 *   label is a short human-readable string suitable for chip text +
 *   tooltip handoff.
 *
 * Tone resolution (in priority order — first match wins; the order
 * matches the PRV-4 pack spec):
 *
 *   1. 'doc'       — `status === 'verified-from-doc'` AND ≥1
 *                    `linkedDocumentRefs` carrying a `docId`. Label
 *                    surfaces the primary ref's filename + page when
 *                    known: `Doc: Phase_I_ESA.pdf p.12`.
 *                    NOTE: the filename is passed through verbatim —
 *                    callers that want laundered prose route through
 *                    `cleanDocFileName` separately. Keeping this
 *                    module dependency-free preserves the no-React
 *                    contract and lets unit tests assert on the raw
 *                    docId without a launderer in the loop.
 *
 *   2. 'authored'  — `status === 'verified-from-context'` AND ≥1
 *                    `authoredClaims` entry, OR `pendingAmendment` is
 *                    set. Authored beats provider so a user-typed
 *                    override never reads as "from provider". Label
 *                    surfaces actor + date: `Authored 2026-05-12
 *                    (william@example.com)`. When `pendingAmendment`
 *                    drives the tone, the label reflects the
 *                    amendment author + createdAt.
 *
 *   3. 'research'  — `status === 'verified-from-research'` AND a
 *                    `researchSource` is attached. Label surfaces the
 *                    source title (or URL) so the chip itself answers
 *                    "where did this come from". Example: `Research:
 *                    NREL Solar Atlas`.
 *
 *   4. 'dam'       — `status === 'verified-from-dam'`. Curated DAM
 *                    entry — label surfaces the DAM source key (the
 *                    `source` field carries the DAM identifier post-
 *                    DCO-1 ingest). Example: `DAM: feasibility-baseline`.
 *
 *   5. 'provider'  — `status === 'verified'` AND `source` is truthy.
 *                    Provider-only verified — label surfaces the
 *                    provider source token: `Provider: Open-Meteo`.
 *
 *   6. 'missing'   — everything else (`unavailable`, `anomalous` with
 *                    no doc backstop, an unknown status). Label is
 *                    the em-dash `'—'` so the chip stays visible as a
 *                    placeholder without leaking noise.
 *
 * Tie-breakers — when a row carries data for multiple tones:
 *
 *   - A `verified-from-doc` row with `linkedDocumentRefs` ALWAYS wins
 *     `doc` even if `pendingAmendment` is also set. Rationale: PRV-4
 *     surfaces what's in the SED right now; the pending amendment
 *     lives in its own pill (`pendingAmendment` chip in the row).
 *
 *   - A `verified-from-context` row with `authoredClaims` wins
 *     `authored` over `provider` even if `source` is set. The status
 *     IS the authoritative signal — provider tokens may linger on
 *     context-derived rows when the prior provider sweep ran.
 *
 *   - A row with status `verified-from-doc` but ZERO usable
 *     `linkedDocumentRefs` (legacy SEDs pre-REF-LINEAGE) falls through
 *     to `missing` rather than `provider`, because the doc tier was
 *     promised but no evidence is left. This is deliberately
 *     conservative — surfacing `Provider: …` on a row the SED tags as
 *     doc-derived would mislead.
 *
 * Pure. Idempotent. Unit-tested per-tone (one test per tone + tie-
 * breakers).
 */

/* ── Public types ──────────────────────────────────────────────────── */

export type SourceTone =
  | 'doc'
  | 'provider'
  | 'authored'
  | 'research'
  | 'dam'
  | 'missing';

export interface SourceLabelResult {
  /** Human-readable chip text. ≤ ~80 chars in practice. */
  label: string;
  /** Tone discriminator — drives CSS via `data-tone={tone}`. */
  tone: SourceTone;
}

/* ── Helpers (pure) ────────────────────────────────────────────────── */

/**
 * Format an authored-claim row's actor field. `writtenBy` can be a
 * string, an object with `uid` + optional `email`, or undefined.
 * Returns a stable token the chip can show — email preferred, uid
 * fallback, `'Site Context'` when nothing is available.
 */
function formatActor(
  writtenBy?: { uid: string; email?: string } | string,
): string {
  if (!writtenBy) return 'Site Context';
  if (typeof writtenBy === 'string') return writtenBy;
  return writtenBy.email ?? writtenBy.uid;
}

/**
 * Extract an ISO date-only string (`YYYY-MM-DD`) from an ISO timestamp
 * or undefined. Defensive — guards against bogus inputs without
 * throwing.
 */
function formatDate(iso?: string): string | undefined {
  if (typeof iso !== 'string' || iso.length === 0) return undefined;
  // Be tolerant of full ISO timestamps OR date-only strings.
  if (/^\d{4}-\d{2}-\d{2}/.test(iso)) return iso.slice(0, 10);
  return undefined;
}

/* ── Public API ────────────────────────────────────────────────────── */

/**
 * Project a `SEDVariable` row into its chip `{ label, tone }`. Pure.
 *
 * Tone resolution priority: doc → authored → research → dam → provider
 * → missing. See module-level docstring for the per-tone rules.
 */
export function sourceLabel(variable: SEDVariable): SourceLabelResult {
  if (variable.documentFieldSource) {
    const source = variable.documentFieldSource;
    return { tone: 'doc', label: `Doc: ${source.docName}${source.pageNumber != null ? ` p.${source.pageNumber}` : ''}` };
  }
  // ── 1. doc ──────────────────────────────────────────────────────
  // Status takes precedence; the ref array must carry at least one
  // entry with a `docId` to claim the doc tone.
  if (variable.status === 'verified-from-doc') {
    const primary = variable.linkedDocumentRefs?.[0];
    if (primary && primary.docId) {
      const pagePart =
        Number.isFinite(primary.pageNumber) && (primary.pageNumber ?? 0) > 0
          ? ` p.${primary.pageNumber}`
          : '';
      return {
        tone: 'doc',
        label: `Doc: ${primary.docId}${pagePart}`,
      };
    }
    // Legacy SED pre-REF-LINEAGE — fall through to `missing` rather
    // than mislabel as `provider`. The status promised doc-tier but
    // no ref survived the projection.
    return { tone: 'missing', label: '—' };
  }

  // ── 2. authored ─────────────────────────────────────────────────
  // `verified-from-context` is the primary trigger; a pending
  // amendment carries an authored signal too even when the status
  // hasn't flipped yet.
  if (variable.status === 'verified-from-context') {
    const claim = variable.authoredClaims?.[0];
    if (claim) {
      const actor = formatActor(claim.writtenBy);
      const dateStr = formatDate(claim.lastWrittenAt);
      const datePart = dateStr ? `${dateStr} ` : '';
      return {
        tone: 'authored',
        label: `Authored ${datePart}(${actor})`.replace(/\s+\(/, ' ('),
      };
    }
    // Status said context but no claim survived — still authored
    // tier so the chip stays correct visually; label degrades to a
    // generic.
    return { tone: 'authored', label: 'Authored' };
  }
  if (variable.pendingAmendment) {
    // Pending amendment supersedes provider-only verified rows so the
    // chip reflects the in-flight user action.
    const amend = variable.pendingAmendment;
    const dateStr = formatDate(amend.createdAt);
    const actor = amend.createdByEmail || amend.createdBy || 'user';
    const datePart = dateStr ? `${dateStr} ` : '';
    return {
      tone: 'authored',
      label: `Authored ${datePart}(${actor})`.replace(/\s+\(/, ' ('),
    };
  }

  // ── 3. research ─────────────────────────────────────────────────
  if (variable.status === 'verified-from-research') {
    const r = variable.researchSource;
    if (r) {
      const title = r.sourceTitle || r.sourceUrl;
      return {
        tone: 'research',
        label: `Research: ${title}`,
      };
    }
    return { tone: 'research', label: 'Research' };
  }

  // ── 4. dam ──────────────────────────────────────────────────────
  if (variable.status === 'verified-from-dam') {
    const src = variable.source ? `: ${variable.source}` : '';
    return { tone: 'dam', label: `DAM${src}` };
  }

  // ── 5. report-derived ──────────────────────────────────────────
  // `verified-from-report` (RDX-1) doesn't appear in the PRV-4 pack's
  // six-tone enumeration. Treat it as `authored`-tier in spirit (it's
  // a curated value carried forward by a prior generation) but expose
  // it through the `provider` tone so the existing six-tone CSS
  // doesn't need to grow. Note in label.
  if (variable.status === 'verified-from-report') {
    return { tone: 'provider', label: 'Prior report' };
  }

  // ── 6. provider ────────────────────────────────────────────────
  if (variable.status === 'verified' && variable.source) {
    return { tone: 'provider', label: `Provider: ${variable.source}` };
  }

  // ── 7. missing (fallthrough) ───────────────────────────────────
  return { tone: 'missing', label: '—' };
}
